import React, { useState, useEffect, useRef, useMemo } from "react";
import Select, {
  CSSObjectWithLabel,
  GroupBase,
  StylesConfig,
} from "react-select";
import { useTranslation } from "react-i18next";
import ClipLoader from "react-spinners/ClipLoader";
import axiosClient from "../../libs/axiosClient";
import styles from "./ChatModal.module.scss";
import terms from "../../language/terms.json";
import chinaFlag from "../../assets/hospitalReservation/chinaFlag.png";
import japanFlag from "../../assets/hospitalReservation/japanFlag.png";
import ukFlag from "../../assets/hospitalReservation/ukFlag.png";
import arabFlag from "../../assets/hospitalReservation/arabFlag.png";
import kakaoIcon from "../../assets/hospitalReservation/kakaotalkIcon.png";
import whatsappIcon from "../../assets/hospitalReservation/whatsappIcon.png";
import lineIcon from "../../assets/hospitalReservation/lineIcon.png";
import wechatIcon from "../../assets/hospitalReservation/wechatIcon.png";

interface ChatModalProps {
  onClose: () => void;
  hospitalId: string;
  hospitalName: string;
  profileImage: string;
}

const customStyles: StylesConfig<
  {
    value: string;
    label: JSX.Element;
  },
  false,
  GroupBase<{ value: string; label: JSX.Element }>
> = {
  control: (provided: CSSObjectWithLabel) => ({
    ...provided,
    padding: "0",
    border: "1px solid #ccc",
    borderRadius: "5px",
    width: "100%",
    boxSizing: "border-box",
    fontSize: window.innerWidth <= 768 ? "12px" : "16px",
    height: window.innerWidth <= 768 ? "36px" : "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  menu: (provided: CSSObjectWithLabel) => ({
    ...provided,
    zIndex: 5,
  }),
  option: (provided: CSSObjectWithLabel, state) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    backgroundColor: state.isFocused ? "#f0f0f0" : "white",
    color: "#333",
    padding: "10px",
    fontSize: window.innerWidth <= 768 ? "12px" : "16px",
  }),
  dropdownIndicator: (provided: CSSObjectWithLabel) => ({
    ...provided,
    padding: "8px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
};

const ChatModal: React.FC<ChatModalProps> = ({ onClose, hospitalId, hospitalName, profileImage }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [passportName, setPassportName] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedMessenger, setSelectedMessenger] = useState<string | null>(
    null
  );
  const [messengerID, setMessengerID] = useState("");
  const [messages, setMessages] = useState<any[]>([]);
  const [historyMessages, setHistoryMessages] = useState<any[]>([]);
  const [termsChecked, setTermsChecked] = useState<boolean[]>(Array(terms.length).fill(false));
  const [openTerms, setOpenTerms] = useState<number | null>(null);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [newMessage, setNewMessage] = useState("");
  // const [receiverId, setReceiverId] = useState("");
  const [userType, setUserType] = useState("user");
  const chatLogRef = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const { t, i18n } = useTranslation();
  
  // 실제 병원 아이디
  const receiverId = hospitalId;
  
  //테스트용 병원 아이디
  // const receiverId = 15;

  // 소켓 연결
  const token = localStorage.getItem("token");
  const websocketUrl = "wss://a71g9968f1.execute-api.ap-northeast-2.amazonaws.com/development/";
  
  const connectWebSocket = () => {
    if (!token) {
      console.error("Token not available");
      return;
    }
  
    const url = `${websocketUrl}?user_type=${userType}&receiver_id=${receiverId}&token=${token}`;
    const ws = new WebSocket(url);
  
    ws.onopen = () => {
      console.log("Connected to WebSocket");

      const getChatHistoryMessage = {
        action: "getChatHistory",
        user_type: userType,
        token,
        receiver_id: receiverId,
      };
      
      ws.send(JSON.stringify(getChatHistoryMessage));
      setIsLoading(true);
    };
  
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log("Message received:", data);
  
      switch (data.action) {
        case "sendMessage":
          if (data.sender_user_type === "hospital") {
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                user_id: "their",
                message: data.message,
                timestamp: data.timestamp || new Date().toISOString(),
                message_id: data.message_id,
              },
            ]);
          } else {
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                user_id: data.user_id || "me",
                message: data.message,
                timestamp: data.timestamp || new Date().toISOString(),
                message_id: data.message_id,
                isTranslating: true,
              },
            ]);
          }
          break;
        
        // case "setTranslatedMessage":
        //   setMessages((prevMessages) => {
        //     const updatedMessages = [...prevMessages];

        //     const originalMessageIndex = updatedMessages.findIndex(
        //       (msg) => msg.message_id === data.message_id
        //     );

        //     if (originalMessageIndex !== -1) {
        //       const isAlreadyTranslated = updatedMessages.some(
        //         (msg, idx) =>
        //           idx > originalMessageIndex &&
        //           msg.user_id === "me_translated" &&
        //           msg.message_id === data.message_id
        //       );
  
        //       if (!isAlreadyTranslated) {
        //         updatedMessages.splice(originalMessageIndex + 1, 0, {
        //           user_id: "me_translated",
        //           message: data.translated_message,
        //           timestamp: new Date().toISOString(),
        //           message_id: data.message_id,
        //           isTranslating: false,
        //         });
                
        //         updatedMessages[originalMessageIndex].isTranslating = false;
        //       }
        //     } else {
        //       console.warn(
        //         "Original message for translation not found:",
        //         data.message_id
        //       );
        //     }
  
        //     return updatedMessages;
        //   });
        //   break;
        
        case "setTranslatedMessage":
          setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];
        
            const originalMessageIndex = updatedMessages.findIndex(
              (msg) => msg.message_id === data.message_id
            );
        
            if (originalMessageIndex !== -1) {
              const isAlreadyTranslated = updatedMessages.some(
                (msg, idx) =>
                  idx > originalMessageIndex &&
                  msg.user_id ===
                    (data.sender_user_type === "hospital" ? "their" : "me_translated") &&
                  msg.message_id === data.message_id
              );
        
              if (!isAlreadyTranslated) {
                updatedMessages.splice(originalMessageIndex + 1, 0, {
                  user_id: data.sender_user_type === "hospital" ? "their" : "me_translated",
                  message: data.translated_message,
                  timestamp: new Date().toISOString(),
                  message_id: data.message_id,
                  isTranslating: false,
                });
                
                console.log("아직 번역 안됨: ", updatedMessages[originalMessageIndex]);
                
                console.log("번역되어야 할 메시지: ", updatedMessages[originalMessageIndex + 1]);
                
                console.log(updatedMessages[originalMessageIndex + 1].translated);
        
                updatedMessages[originalMessageIndex].isTranslating = false;
                
                if (updatedMessages[originalMessageIndex + 1].user_id === "their") {
                  updatedMessages[originalMessageIndex + 1].user_id = "their_translated";  
                }
                
                // updatedMessages[originalMessageIndex + 1].user_id = "their_translated";
                
                console.log("번역된 메시지의 user_id: ", updatedMessages[originalMessageIndex + 1].user_id);
              }
            } else {
              console.warn(
                "Original message for translation not found:",
                data.message_id
              );
            }
        
            return updatedMessages;
          });
          break;
        
          case "getChatHistory":
            if (Array.isArray(data.history) && data.history.length > 0) {
              const mappedMessages = data.history.map((msg: any) => ({
                message_id: msg.message_id,
                original: msg.message,
                translated: msg.user_id ? msg.user_translated_message : msg.hospital_translated_message,
                timestamp: msg.timestamp,
                user_type: msg.user_id ? "user" : "hospital",
                isTranslating: false,
              }));

              setHistoryMessages(mappedMessages);
              setStep(2);
            } else {
              console.log("No chat history found. Starting new chat.");
              setHistoryMessages([]);
              setStep(1);
            }

            setIsLoading(false);
            break;

        default:
          console.warn("Unhandled WebSocket action:", data.action);
          break;
      }
    };
  
    ws.onclose = () => {
      console.log("WebSocket closed");
    };
  
    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };
  
    setSocket(ws);
  };
  
  const sendMessage = () => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      const messageData = {
        action: "sendMessage",
        receiver_id: receiverId,
        user_type: userType,
        token,
        message: newMessage,
        send_user_type: "user",
        translate_and_save: true,
        target_lang: "EN-US",
      };
  
      const localMessageId = Date.now();

      socket.send(JSON.stringify({ ...messageData }));
  
      setMessages((prev) => [
        ...prev,
        {
          user_id: "me",
          message: newMessage,
          timestamp: new Date().toISOString(),
          message_id: localMessageId,
        },
      ]);
  
      setNewMessage("");
    } else {
      console.log("WebSocket is not connected");
    }
  };
  
  const closeWebSocket = () => {
    if (socket) {
      socket.close();
    }
  };

  useEffect(() => {
    setIsLoading(true);
    connectWebSocket();
    
    return () => socket?.close();
  }, []);
  
  useEffect(() => {
    if (chatLogRef.current && historyMessages.length > 0) {
      chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
      setIsLoading(false);
    }
  }, [historyMessages]);

  useEffect(() => {
    if (chatLogRef.current) {
      chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
    }
  }, [messages]);
  
  const isFormValid = useMemo(() => {
    return (
      passportName.trim() &&
      selectedCountry &&
      phoneNumber.trim() &&
      selectedMessenger &&
      messengerID.trim() &&
      termsChecked.every((checked) => checked)
    );
  }, [passportName, selectedCountry, phoneNumber, selectedMessenger, messengerID, termsChecked]);

  const validateForm = () => {
    const newErrors: string[] = [];
  
    if (!passportName.trim()) newErrors.push("Passport name is required.");
    if (!selectedCountry) newErrors.push("Country is required.");
    if (!phoneNumber.trim()) newErrors.push("Phone number is required.");
    if (!selectedMessenger) newErrors.push("Messenger is required.");
    if (!messengerID.trim()) newErrors.push("Messenger ID is required.");
    if (!termsChecked.every((checked) => checked)) newErrors.push("All terms must be accepted.");
  
    setErrors(newErrors);
  
    return newErrors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      console.log("Form has errors:", errors);
      return;
    }

    try {
      const response = await axiosClient.post(
        "/api/v1/reservation",
        {
          name: passportName,
          country_name: selectedCountry,
          phone: phoneNumber,
          messenger_type: selectedMessenger,
          messenger_id: messengerID,
          hospital_id: receiverId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log("Chat started successfully:", response.data);
      setStep(2);
    } catch (error: any) {
      console.error("Error starting chat:", error.response?.data || error);
    }
  };

  const countryOptions = [
    {
      value: "japan",
      label: (
        <div className={styles.option}>
          <img src={japanFlag} alt="Japan" className={styles.flag} />
          Japanese
        </div>
      ),
    },
    {
      value: "china",
      label: (
        <div className={styles.option}>
          <img src={chinaFlag} alt="China" className={styles.flag} />
          Chinese
        </div>
      ),
    },
    {
      value: "arab",
      label: (
        <div className={styles.option}>
          <img src={arabFlag} alt="Arab" className={styles.flag} />
          Arabic
        </div>
      ),
    },
    {
      value: "uk",
      label: (
        <div className={styles.option}>
          <img src={ukFlag} alt="UK" className={styles.flag} />
          English
        </div>
      ),
    },
  ];

  const messengerOptions = [
    {
      value: "kakao",
      label: (
        <div className={styles.option}>
          <img src={kakaoIcon} alt="KakaoTalk" className={styles.icon} />
          KakaoTalk
        </div>
      ),
    },
    {
      value: "line",
      label: (
        <div className={styles.option}>
          <img src={lineIcon} alt="Line" className={styles.icon} />
          Line
        </div>
      ),
    },
    {
      value: "wechat",
      label: (
        <div className={styles.option}>
          <img src={wechatIcon} alt="WeChat" className={styles.icon} />
          WeChat
        </div>
      ),
    },
    {
      value: "whatsapp",
      label: (
        <div className={styles.option}>
          <img src={whatsappIcon} alt="WhatsApp" className={styles.icon} />
          WhatsApp
        </div>
      ),
    },
  ];
  
  const toggleTerm = (index: number) => {
    if (openTerms !== index) {
      setOpenTerms(index);
    } else {
      setOpenTerms(null);
    }
  };
  
  const toggleTermChecked = (index: number) => {
    setTermsChecked((prev) => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });
  };
  
  
  useEffect(() => {
    const savedLanguage = localStorage.getItem("selectedLanguage") || "en";
    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose}>
          &times;
        </button>
  
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <ClipLoader size={50} color="#007bff" />
            <p>{i18n.t("chatModal.loading.loadingText")}</p>
          </div>
        ) : step === 1 ? (
          <div className={styles.stepOne}>
            <h2>{i18n.t("chatModal.step1.title")}</h2>
            <p>{i18n.t("chatModal.step1.description")}</p>
            <form>
              <div className={styles.formGroup}>
                <label className={styles.labelWithLanguage}>
                  <span className={styles.required}>
                    {i18n.t("chatModal.step1.required")}
                  </span>{" "}
                  {i18n.t("chatModal.step1.passportName")}
                  <span className={styles.language}>
                    {i18n.t("chatModal.step1.english")}
                  </span>
                </label>
                <input
                  type="text"
                  placeholder="Passport Name"
                  onChange={(e) => setPassportName(e.target.value)}
                />
              </div>
  
              <div className={styles.formGroup}>
                <label>
                  <span className={styles.required}>
                    {i18n.t("chatModal.step1.required")}
                  </span>{" "}
                  {i18n.t("chatModal.step1.country")}
                </label>
                <Select
                  options={countryOptions}
                  styles={customStyles}
                  placeholder={i18n.t("chatModal.step1.countryPlaceholder")}
                  value={countryOptions.find(
                    (option) => option.value === selectedCountry
                  )}
                  classNamePrefix="react-select"
                  onChange={(option) =>
                    setSelectedCountry(option?.value || null)
                  }
                />
              </div>
  
              <div className={styles.formGroup}>
                <label>
                  <span className={styles.required}>
                    {i18n.t("chatModal.step1.required")}
                  </span>{" "}
                  {i18n.t("chatModal.step1.phoneNumber")}
                </label>
                <input
                  type="tel"
                  placeholder="+81 Phone Number"
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </div>
  
              <div className={styles.formGroup}>
                <label>
                  <span className={styles.required}>
                    {i18n.t("chatModal.step1.required")}
                  </span>{" "}
                  {i18n.t("chatModal.step1.messenger")}
                </label>
                <Select
                  options={messengerOptions}
                  styles={customStyles}
                  placeholder={i18n.t("chatModal.step1.messengerPlaceholder")}
                  classNamePrefix="react-select"
                  onChange={(option) =>
                    setSelectedMessenger(option?.value || null)
                  }
                />
              </div>
  
              <div className={styles.formGroup}>
                <label>
                  <span className={styles.required}>
                    {i18n.t("chatModal.step1.required")}
                  </span>{" "}
                  {i18n.t("chatModal.step1.messengerID")}
                </label>
                <input
                  type="text"
                  placeholder={i18n.t("chatModal.step1.messengerIDPlaceholder")}
                  onChange={(e) => setMessengerID(e.target.value)}
                />
              </div>
              <div className={styles.terms}>
                {terms.map((term, index) => (
                  <div key={index}>
                    <div className={styles.termRow}>
                      <div className={styles.termContent}>
                        <input 
                          type="checkbox"
                          id={`term-${index}`}
                          checked={termsChecked[index]}
                          onChange={() => toggleTermChecked(index)}
                        />
                        <label htmlFor={`term-${index}`}>
                          <span className={styles.required}>
                            {i18n.t("chatModal.step1.required")}
                          </span>{" "}
                          {term.title}
                        </label>
                      </div>
                      <button
                        type="button"
                        className={styles.viewDetailsButton}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleTerm(index);
                        }}
                      >
                        {openTerms === index
                          ? i18n.t("chatModal.step1.hideDetails")
                          : i18n.t("chatModal.step1.viewDetails")}
                      </button>
                    </div>
                    <div
                      className={`${styles.termDetails} ${
                        openTerms === index ? styles.expanded : ""
                      }`}
                    >
                      <p>{term.content}</p>
                    </div>
                    {index < terms.length - 1 && <div className={styles.divider}></div>}
                  </div>
                ))}
              </div>
              <button
                type="button"
                className={styles.submitButton}
                onClick={handleSubmit}
                style={{
                  backgroundColor: isFormValid ? "#007bff" : "#999",
                  cursor: isFormValid ? "pointer" : "not-allowed",
                }}
                disabled={!isFormValid}
              >
                {i18n.t("chatModal.step1.agreeAllAndStart")}
              </button>
            </form>
          </div>
        ) : (
          <div className={styles.stepTwo}>
            <header className={styles.chatHeader}>
              <img
                src={profileImage || "/path/to/default-image.jpg"}
                alt="Clinic"
                className={styles.clinicImage}
              />
              <h3>{hospitalName}</h3>
            </header>
            <div className={styles.divider} />
            <div className={styles.chatMessages} ref={chatLogRef}>
              {historyMessages.map((msg, index) => (
                <div key={msg.message_id || index}>
                  <div
                    className={
                      msg.user_type === "user"
                        ? styles.myMessageWrapper
                        : styles.theirMessageWrapper
                    }
                  >
                    <div className={styles.messageBubble}>{msg.original}</div>
                    <span
                      className={
                        msg.user_type === "user"
                          ? styles.messageTimestampLeft
                          : styles.messageTimestampRight
                      }
                    >
                      {new Date(msg.timestamp).toLocaleDateString("ko-KR", {
                        year: "2-digit",
                        month: "2-digit",
                        day: "2-digit",
                      })}
                      <br />
                      {new Date(msg.timestamp).toLocaleTimeString("ko-KR", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}
                    </span>
                  </div>
                  {msg.translated?.trim() && (
                    <div
                      className={styles.translatedMessageWrapper}
                      style={{
                        textAlign: msg.user_type === "hospital" ? "left" : "right",
                        marginLeft: msg.user_type === "hospital" ? "0" : undefined,
                      }}
                    >
                      <div className={styles.messageBubble}>{msg.translated}</div>
                    </div>
                  )}
                </div>
              ))}
              {messages.map((msg, index) => (
              <div key={index}>
                {/* 내가 보낸 메시지 */}
                {msg.user_id === "me" && (
                  <div className={styles.myMessageWrapper}>
                    <div className={styles.messageBubble}>{msg.message}</div>
                    <span className={styles.messageTimestampLeft}>
                      {new Date(msg.timestamp).toLocaleDateString("ko-KR", {
                        year: "2-digit",
                        month: "2-digit",
                        day: "2-digit",
                        timeZone: "Asia/Seoul",
                      }).replace(/\.$/, "")}
                      <br />
                      {new Date(msg.timestamp).toLocaleTimeString("ko-KR", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                        timeZone: "Asia/Seoul",
                      })}
                    </span>
                  </div>
                )}

                {/* 번역 중인 메시지 */}
                {msg.isTranslating && (
                  <div className={styles.translatedMessageWrapper}>
                    <div className={styles.translatingMessage}>
                      <div className={styles.spinner}></div>
                      <span>{i18n.t("chatModal.loading.translating")}</span>
                    </div>
                  </div>
                )}

                {/* 내가 보낸 번역된 메시지 */}
                {msg.user_id === "me_translated" && (
                  <div className={styles.translatedMessageWrapper}>
                    <div className={styles.messageBubble}>{msg.message}</div>
                  </div>
                )}

                {/* 상대방이 보낸 메시지 */}
                {msg.user_id === "their" && (
                  <div className={styles.theirMessageWrapper}>
                    {/* 원문 */}
                    <div className={styles.messageBubble}>{msg.message}</div>

                    {/* 번역문이 있을 경우 */}
                    {/* {msg.translated && (
                      <div className={styles.translatedText}>{msg.translated}</div>
                    )} */}

                    {/* 시간 */}
                    <span className={styles.messageTimestampRight}>
                      {new Date(msg.timestamp).toLocaleDateString("ko-KR", {
                        year: "2-digit",
                        month: "2-digit",
                        day: "2-digit",
                        timeZone: "Asia/Seoul",
                      }).replace(/\.$/, "")}
                      <br />
                      {new Date(msg.timestamp).toLocaleTimeString("ko-KR", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}
                    </span>
                  </div>
                )}

                {msg.user_id === "their_translated" && (
                  <div className={styles.theirTranslatedMessageWrapper}>
                    <div className={styles.theirTranslatedMessage}>
                      {msg.message}
                    </div>
                  </div>
                )}
              </div>
            ))}
            </div>
            <div className={styles.chatInput}>
              <div className={styles.inputContainer}>
                <textarea
                  placeholder={i18n.t("chatModal.step2.messagePlaceholder")}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  rows={3}
                />
                <button
                  className={styles.sendButton}
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                >
                  &#9650;
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatModal;
